import type { Contact, ContactKind, ContactRelationshipType } from '../data/contactTypes';
import { CONTACT_RELATIONSHIP_TYPES } from '../data/contactTypes';
import type { ClientOverviewContactRow } from './getClientOverviewContacts';

function relatedPersonIdFromOverviewRow(row: ClientOverviewContactRow): string | undefined {
  if (row.dedupeKey.startsWith('rp:')) return row.dedupeKey.slice(3);
  const refPrefix = 'ref:RelatedPerson/';
  if (row.dedupeKey.startsWith(refPrefix)) return row.dedupeKey.slice(refPrefix.length);
  return undefined;
}

function coerceRelationshipType(raw: string): ContactRelationshipType | undefined {
  if ((CONTACT_RELATIONSHIP_TYPES as readonly string[]).includes(raw)) {
    return raw as ContactRelationshipType;
  }
  if (raw === 'Father' || raw === 'Mother') return 'Parent';
  return undefined;
}

function relationshipTypeFromRow(row: ClientOverviewContactRow): ContactRelationshipType {
  for (const segment of row.segments) {
    const role = segment.roleLabel?.trim();
    if (!role || role === 'Guarantor' || /emergency/i.test(role)) continue;
    const coerced = coerceRelationshipType(role);
    if (coerced) return coerced;
  }
  return 'Other';
}

function contactKindFromOverviewRow(row: ClientOverviewContactRow): ContactKind {
  if (row.guarantorBillingProfileClientId || row.segments.some((s) => s.roleLabel === 'Guarantor')) {
    return 'guarantor';
  }
  if (row.dedupeKey.startsWith('pc:')) return 'emergency-contact';
  return 'related-person';
}

function roleFromRow(row: ClientOverviewContactRow, relationshipType: ContactRelationshipType): string | undefined {
  if (relationshipType !== 'Other' && relationshipType !== 'Physician') return undefined;
  const leftover = row.segments
    .map((s) => s.roleLabel?.trim())
    .find((role) => role && role !== 'Guarantor' && !/emergency/i.test(role) && !coerceRelationshipType(role));
  return leftover;
}

/** Build a profile `Contact` from a chart/overview contact row (for ROI and view modal). */
export function contactFromOverviewRow(row: ClientOverviewContactRow): Contact {
  const raw = row.displayName.trim();
  const parts = raw.split(/\s+/).filter(Boolean);
  const legalFirstName = parts[0] ?? '—';
  const legalLastName = parts.length > 1 ? parts.slice(1).join(' ') : '—';
  const relationshipType = relationshipTypeFromRow(row);
  const contactKind = contactKindFromOverviewRow(row);
  const role = roleFromRow(row, relationshipType);

  if (row.dedupeKey.startsWith('pc:')) {
    return {
      id: `pc-${row.dedupeKey.slice(3)}`,
      source: 'patient-contact',
      contactKind,
      legalFirstName,
      legalLastName,
      relationshipType,
      isEmergencyContact: true,
      phones: row.phone ? [{ value: row.phone, type: 'Mobile' }] : undefined,
      emails: row.email ? [row.email] : undefined,
      notes: row.episodesOfCareSummary !== '—' ? `Roles: ${row.episodesOfCareSummary}` : undefined,
      role,
    };
  }

  const relatedPersonId = relatedPersonIdFromOverviewRow(row);
  return {
    id: relatedPersonId ? `rp-${relatedPersonId}` : `synthetic:${row.dedupeKey}`,
    source: 'related-person',
    relatedPersonId,
    contactKind,
    legalFirstName,
    legalLastName,
    relationshipType,
    isEmergencyContact: false,
    phones: row.phone ? [{ value: row.phone, type: 'Mobile' }] : undefined,
    emails: row.email ? [row.email] : undefined,
    notes: row.episodesOfCareSummary !== '—' ? `Roles: ${row.episodesOfCareSummary}` : undefined,
    role,
  };
}

/**
 * Minimal `Contact` for Medical Record when no profile record exists (e.g. external guarantor).
 */
export function syntheticContactFromOverviewRow(row: ClientOverviewContactRow): Contact {
  return contactFromOverviewRow(row);
}
